/** The signed-in user (`GET /v1/me`) and the organisation the dashboard is showing. */
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, unwrap, type S } from "./client";

const ORG_KEY = "solaros.org";

interface OrgState {
  me: S["Me"];
  isPlatformAdmin: boolean;
  memberships: S["Membership"][];
  membership: S["Membership"] | null;
  orgId: string | null;
  /** owner/admin (or platform admin): may change settings, keys, buy credits. */
  canManage: boolean;
  selectOrg: (id: string) => void;
}

const OrgContext = createContext<OrgState | null>(null);

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => unwrap(api.GET("/v1/me")), staleTime: 60_000 });
}

export function OrgProvider({ me, children }: { me: S["Me"]; children: ReactNode }) {
  const [selected, setSelected] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ORG_KEY);
    } catch {
      return null;
    }
  });
  const memberships = me.memberships;
  const membership = memberships.find((m) => m.organization_id === selected) ?? memberships[0] ?? null;

  useEffect(() => {
    if (membership && membership.organization_id !== selected) setSelected(membership.organization_id);
  }, [membership, selected]);

  const value = useMemo<OrgState>(() => {
    const isPlatformAdmin = me.platform_role === "platform_admin" || me.user.platform_role === "platform_admin";
    return {
      me,
      isPlatformAdmin,
      memberships,
      membership,
      orgId: membership?.organization_id ?? null,
      canManage: isPlatformAdmin || membership?.role === "owner" || membership?.role === "admin",
      selectOrg: (id: string) => {
        localStorage.setItem(ORG_KEY, id);
        setSelected(id);
      },
    };
  }, [me, memberships, membership]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg outside OrgProvider");
  return ctx;
}

/** The current organisation id; pages under the org shell always have one. */
export function useOrgId(): string {
  const { orgId } = useOrg();
  if (!orgId) throw new Error("No organisation selected");
  return orgId;
}
